import { Role } from '@customTypes/types';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { Box, Button, Modal, Text } from '@mantine/core';
import { translate } from '@shared/i18n/translate';
import { useEffect, useState } from 'react';

import { usePushNotifications } from '../hooks/usePushNotifications';
import { useProfileStore } from '../store/profileStore';

interface PushNotificationPromptProps {
  minimalForAdmin?: boolean; // показывать только кнопку для админа
}

export const PushNotificationPrompt = ({
  minimalForAdmin = false
}: PushNotificationPromptProps) => {
  const {
    isAuthenticated,
    isSubscribed,
    subscriptionChecked,
    setIsSubscribed,
    setSubscriptionChecked,
    profile
  } = useProfileStore();

  const { requestPermissionAndRegister } = usePushNotifications();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    (async () => {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setIsSubscribed(!!sub);
        setSubscriptionChecked(true);
      }
    })();
  }, []);

  // проверка подписки и localStorage
  useEffect(() => {
    (async () => {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setIsSubscribed(!!sub);
        setSubscriptionChecked(true);

        // только для артистов/клубов, не подписанных
        if (
          profile &&
          (profile.role === Role.artist || profile.role === Role.club) &&
          !sub
        ) {
          const lastDismiss = localStorage.getItem('pushDismissAt');
          if (
            !lastDismiss ||
            Date.now() - parseInt(lastDismiss, 10) > 24 * 60 * 60 * 1000
          ) {
            setIsOpen(true);
          }
        }
      }
    })();
  }, [profile]);

  if (!profile || !isAuthenticated || isSubscribed || !subscriptionChecked)
    return null;

  // Только кнопка для админа
  if (profile.role === Role.admin && minimalForAdmin) {
    return (
      <Button
        size='xs'
        onClick={async () => {
          await requestPermissionAndRegister();
          setIsSubscribed(true);
        }}
      >
        {translate('commons.allow_note')}
      </Button>
    );
  }

  // Fullscreen баннер для артистов и клубов
  // if (profile.role === Role.artist || profile.role === Role.club) {

  // Fullscreen баннер для артистов и клубов
  if ((profile.role === Role.artist || profile.role === Role.club) && isOpen) {
    const handleAccept = async () => {
      await requestPermissionAndRegister();
      setIsSubscribed(true);
      setIsOpen(false);
      localStorage.removeItem('pushDismissAt'); // больше не показывать
    };

    const handleDismiss = () => {
      localStorage.setItem('pushDismissAt', Date.now().toString());
      setIsOpen(false);
    };

    return (
      <Modal
        centered
        opened={isOpen}
        padding='xl'
        radius={16}
        size='sm' // фиксированный размер окна
        withCloseButton={false} // убираем стандартный крестик
        onClose={() => setIsOpen(false)}
      >
        {/* Кастомный крестик */}
        <Button
          variant='subtle'
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            minWidth: 0,
            padding: 0,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer'
          }}
          onClick={handleDismiss}
        >
          <XMarkIcon
            style={{ width: 18, height: 18, color: 'rgba(0,0,0,0.4)' }}
          />
        </Button>
        <br />
        <Text mb='md' size='xl' style={{ fontWeight: '600' }}>
          {translate('commons.allow_note')}
        </Text>

        <Text
          mb='sm'
          size='sm'
          style={{ lineHeight: 1.5, marginBottom: '20px' }}
        >
          {translate('commons.allow_note_1')}
        </Text>

        <Box component='ul' style={{ paddingLeft: 20, marginBottom: 10 }}>
          <Text component='li' size='sm' style={{ lineHeight: 1.5 }}>
            {translate('commons.allow_note_2')}
          </Text>
          <Text component='li' size='sm' style={{ lineHeight: 1.5 }}>
            {translate('commons.allow_note_3')}
          </Text>
          <Text component='li' size='sm' style={{ lineHeight: 1.5 }}>
            {translate('commons.allow_note_4')}
          </Text>
        </Box>

        <Text
          mb='lg'
          size='sm'
          style={{ lineHeight: 1.5, marginBottom: '20px' }}
        >
          {translate('commons.allow_note_5')}
        </Text>

        <Button
          fullWidth
          size='md'
          style={{
            backgroundColor: '#28a745',
            color: '#fff',
            borderRadius: 12
          }}
          onClick={handleAccept}
        >
          <CheckIcon style={{ width: 20, height: 20, marginRight: 8 }} />
          {translate('commons.allow_note')}
        </Button>
      </Modal>
    );
  }

  return null;
};
